import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { appInjector } from '@max/shared/app-injector';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { BusinessUnitCollectionModel } from '@max/shared/models/business-unit/business-unit-collection.model';
import { LocationModel } from '@max/shared/models/business-unit/location.model';
import { MenuItemsService } from '@max/shared/services/menu-items/menu-items.service';
import { ChannelService } from '@max/shared/services/realtime-messaging/channel.service';
import { AppSettings } from '@max/shared/settings/app-settings.model';
import { AppState } from '@max/store/reducers';
import { TransactionQueueActions } from '@max/transaction/state/actions';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { SecurityClaim } from '../models/security-claim.model';
import { SecurityProfile } from '../models/security-profile.model';
import { BuSelectionService } from '../services/bu-selection.service';
import { ClaimsService } from '../services/claims.service';
import { SecurityCacheService } from '../services/security-cache.service';
import { filter, takeUntil } from 'rxjs/operators';
import { InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService } from '@azure/msal-angular';
import { NotificationService } from '@max/notification/services/notification.service';

@Component({
    selector: 'bu-selection',
    templateUrl: './bu-selection.html'
})
/**
 * Allows user to select a business unit and refresh security transaction claims
*/
export class BuSelectionComponent extends BaseComponent implements OnInit {

    private businessUnitCollection: BusinessUnitCollectionModel;
    public businessUnits: LocationModel[];
    private securityClaims: SecurityClaim[];
    private acsClaims: SecurityClaim[];
    private securityProfile: SecurityProfile;
    public businessUnitsLoading = false;
    private appSettings: AppSettings;
    public selectedBusinessUnit: any = '';
    public windows: any;
    public currentWindow: any;
    public windowLoaded = false;
    private readonly _destroying$ = new Subject<void>();
    // Deep Link trigger variables
    private hasSecurityTransactionClaimsCompleted = false;
    private hasACSTransactionClaimsCompleted = false;
    private hasMultipleTransactionClaimsCompleted = false;

    // Subject for DeepLink
    private allClaimsDownloaded: BehaviorSubject<SecurityClaim[]>;
    private queryParamsCollection = [];

    constructor(
        private buSelectionService: BuSelectionService,
        private router: Router,
        private _claimsService: ClaimsService,
        private menuItemsService: MenuItemsService,
        private _channelService: ChannelService,
        private store: Store<AppState>,
        private _msalBroadcastService: MsalBroadcastService,
        private notificationService: NotificationService,
    ) {
        super();
        this.allClaimsDownloaded = new BehaviorSubject<SecurityClaim[]>(null);
    }

    ngOnInit() {
        this._msalBroadcastService.inProgress$
            .pipe(
                // Filtering for all interactions to be completed
                filter((status: InteractionStatus) => status === InteractionStatus.None)
                , takeUntil(this._destroying$))
            .subscribe(() => {
                if (this.auth.hasApplicationAccess) {
                    this.getBusinessUnits();
                    const injector = appInjector();
                    this.UseHelpContent('#CONTENT/Cross_Functional/Select_Business_Unit.htm%3FTocPath%3DCross%2520Functional%7C_____5');
                    this.appSettings = <AppSettings>injector.get(AppSettings);
                } else {
                    this.exitLoginProcess();
                }
            },
            (error): void => {
                this.exitLoginProcess();
            });
    }

    private logout() {
        this.auth.logOut();
    }

    cancelBusinessUnitSelection() {
        if (this.auth.validUser && this.auth.businessUnitId != undefined && this.auth.businessUnitId.length > 0) {
            this.enterApplication();
        } else {
            this.logout();
        }
    }

    /**
     * Gets possible business units
     */
    private getBusinessUnits() {
        const self = this;
        this.businessUnitsLoading = true;
        this.buSelectionService.getBusinessUnits().subscribe(businessUnits => {
            self.businessUnitCollection = businessUnits.result;
            self.businessUnits = self.businessUnitCollection.locations;
            const cookieBusinessUnitId = SecurityCacheService.businessUnit;
            if (cookieBusinessUnitId != undefined) {
                self.selectedBusinessUnit = cookieBusinessUnitId;
            } else if (self.businessUnitCollection.primaryBusinessUnit != undefined) {
                self.selectedBusinessUnit = self.businessUnitCollection.primaryBusinessUnit.id + ',' + self.businessUnitCollection.primaryBusinessUnit.businessUnitName;
            }

            // Get Windows for Business Units
            if (self.selectedBusinessUnit != undefined) {
                self.loadWindows(self.selectedBusinessUnit);
            }
        },
        err => {
            self.handleValidationResult(err);
        });
    }

    /**
     * Navigates to the appropriate url when the user has successfully authenticated to the application and have downloaded authorization
     */
    private enterApplication() {
        const self = this;
        self.router.navigate(['/']);
    }

    /**
     * Navigates to the appropriate url when the user has not successfully authenticated to the application
     */
    private exitLoginProcess() {
        const self = this;
        self.router.navigate(['/']);
    }

    countString(count: number): string {
        return count === 0 ? '' : `${count}`;
    }

    /**
     * Stores business unit
     */
    confirmBusinessUnit() {
        const self = this;
        if (this.selectedBusinessUnit != undefined && this.selectedBusinessUnit.length > 0 && this.auth.hasApplicationAccess) {
            // send to the home page
            this.auth.updateBusinessUnit(this.selectedBusinessUnit);
            // Set the windowId in the
            this.auth.updateWindowLocalStorage(this.currentWindow);

            // Set up DeepLink dependencies
            this.allClaimsDownloaded.subscribe(
                item => {
                    if (this.hasACSTransactionClaimsCompleted
                        && this.hasMultipleTransactionClaimsCompleted
                        && this.hasSecurityTransactionClaimsCompleted) {
                        if (!self.deepLinkHookUp()) {
                            self.enterApplication();
                        }
                    }
                },
                error => {
                    self.handleValidationResult(error);
                }
            );

            // get transaction claims
            this._claimsService.getUserIdentity().subscribe(
                securityIdentity => {
                    // Note the above getUserIdentity() returns a Security Stamp which is also the Identity
                    this.auth.updateSecurityStamp(securityIdentity.result);

                    // for Security Transaction Claims
                    self._claimsService.getSecurityClaims().subscribe(
                        profile => {
                            self.securityProfile = profile.result;
                            self.securityClaims = self.securityProfile.securityClaims;

                            // store the claims and notify all subscribees
                            this.auth.updateSecurityTransactions(self.securityClaims);
                            // self.router.navigate(['/']);

                            // Notify deepLink
                            this.hasSecurityTransactionClaimsCompleted = true;
                            this.allClaimsDownloaded.next(null);

                            // Build Menu Items
                            this.getMenuItems();

                            // update notification count with the current selected business unit
                            this.notificationService.getMessageCount().subscribe(result => {
                                this.auth.updateNotificationsCount(this.countString(result));
                            });

                            this._channelService.startSignalR();
                        },
                        err => {
                            self.handleValidationResult(err);
                        }
                    );

                    // For ACS Transaction Claims
                    self._claimsService.getACSClaims().subscribe(
                        profile => {
                            self.securityProfile = profile.result;
                            self.acsClaims = self.securityProfile.acsClaims;

                            // store the claims and notify all subscribees
                            this.auth.updateACSTransactions(self.acsClaims);

                            // Notify deepLink
                            this.hasACSTransactionClaimsCompleted = true;
                            this.allClaimsDownloaded.next(null);
                        },
                        err => {
                            self.handleValidationResult(err);
                        }
                    );

                    // For Multiple Transaction Claims
                    self._claimsService.getMultipleClaims().subscribe(
                        claimSet => {
                            // Assemble ALL claim sets allowable to this user
                            self.securityProfile = claimSet.result;
                            self.acsClaims = self.securityProfile.acsClaims;
                            self.securityClaims = self.securityProfile.securityClaims;

                            // store the claims and notify all subscribees
                            this.auth.updateMultipleClaimSets(self.securityClaims, self.acsClaims);

                            // Notify deepLink
                            this.hasMultipleTransactionClaimsCompleted = true;
                            this.allClaimsDownloaded.next(null);
                        });

                },
                err => {
                    self.handleValidationResult(err);
                }
            );

        }
    }

    private getMenuItems() {
        // For Menu Items
        this.menuItemsService.getMenuItems().subscribe(
            result => {
                localStorage.setItem('menuItems', JSON.stringify(result));
                const storedMenuItems = localStorage.getItem('menuItems');
                this.menuItemsService.sendData(JSON.parse(storedMenuItems));
            },
            error => {}
        );
    }

    /**
     * Navigates to the url the user was trying to go to before being interrupted by the login process
     */
    private deepLinkHookUp(): boolean {

        let retVal = false;

        // let postLogoutRedirectUri: string = 'https://domain.gov/auth/logout';
        const cachedDeepLinkValue = localStorage.getItem('mvmDeepLink');

        // if there is no deep link, then do not do anything
        if (cachedDeepLinkValue != null && cachedDeepLinkValue != undefined && cachedDeepLinkValue != 'null') {
            const postLogoutRedirectUri = this.appSettings.security.postLogoutRedirectUri;
            const routePart = this.getRoutePartFromURL(postLogoutRedirectUri);
            const self = this;
            if (cachedDeepLinkValue == routePart) {
                self.enterApplication();
            } else {
                // If URI has query string then parse the queryParams and navigate using pathaname and queryparams.
                // else, do a conventional routing using pathname.
                if (cachedDeepLinkValue.indexOf('?') > -1) {
                    const queryCollection = this.constructRoutePramsObject(cachedDeepLinkValue);
                    // Example: self.router.navigate(["finance/paymentVoucher/verification"], { queryParams: { PPVNumber1: 10000000, PPVAmount1: 10, PPVNumber2: 10000001, PPVAmount2: 5, PPVNumber3: 10000002, PPVNumber4: 10000003, PPVAmount4: 1, RACFId: "abcdef", Batch: "abcde", OfficeId: 11, Session:22} });
                    self.router.navigate([queryCollection[0]], { queryParams: queryCollection[1]});
                } else {
                    self.router.navigate([cachedDeepLinkValue]);
                }
            }
            // W3C web storage specification
            // The removeItem(key) method must cause the key/ value pair with the given key to be removed
            // from the list associated with the object, if it exists.  If no item with that key exists, the method must do nothing.
            localStorage.removeItem('mvmDeepLink');
            localStorage.removeItem('mvmDeepLinkTemp');

            retVal = true;
        }

        return retVal;
    }

    private getRoutePartFromURL(postLogoutRedirectUri: string): string {
        const tempUrl = document.createElement('a');
        tempUrl.href = postLogoutRedirectUri;
        return tempUrl.pathname;
    }

    private constructRoutePramsObject(Uri: string) {
        // Example : self.router.navigate(["finance/paymentVoucher/verification"], { queryParams: { PPVNumber1: 10000000, PPVAmount1: 10, PPVNumber2: 10000001, PPVAmount2: 5, PPVNumber3: 10000002, PPVNumber4: 10000003, PPVAmount4: 1, RACFId: "abcdef", Batch: "abcde", OfficeId: 11, Session:22} });
        const queryString = Uri.split('?');
        const uri = queryString[0];
        const queryParam = queryString[1];
        const querParamArray = queryParam.split('&');
        const queryParams = {};

        for (let i = 0; i < querParamArray.length; i++) {
            const paramSplitter = querParamArray[i].split('=');
            queryParams[paramSplitter[0]] = paramSplitter[1];
        }

        this.queryParamsCollection.push(uri);
        this.queryParamsCollection.push(queryParams);

        return this.queryParamsCollection;
    }

    buChanged(businessUnit) {
        this.loadWindows(businessUnit);
        localStorage.removeItem('mvd:windowId');
        this.windowLoaded = false;
        this.businessUnitsLoading = true;
    }

    private loadWindows(businessUnit: any) {
        // Take the first character from the string i.e BUID
        const buid = parseInt(businessUnit.split(',')[0]);
        this.buSelectionService.getWindows(buid).subscribe(
            results => {
                this.windows = results.result;
                const cookieWindowId = SecurityCacheService.window;
                if (cookieWindowId && cookieWindowId != '' && cookieWindowId != null) {
                    this.currentWindow = cookieWindowId;
                } else {
                    this.currentWindow = '';
                }

                if (this.windows.length > 0) {
                    this.windowLoaded = true;
                }

                this.businessUnitsLoading = false;
            }
        );
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
